'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import {
  identityFromWif,
  loadIdentity,
  saveIdentity,
  shortAccountId,
  type Identity,
} from '../../lib/identity';
import { createAccount, ApiCallFailed } from '../../lib/api';

// Same whitelist as /signup. Anything outside the whitelist falls
// back to /dashboard so an attacker can't redirect a logging-in
// user to evil.example.
const NEXT_PREFIXES = ['/dashboard', '/subscribe', '/methods'];

function safeNext(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
  const path_only = raw.split('?', 1)[0]!.split('#', 1)[0]!;
  if (!NEXT_PREFIXES.some((p) => path_only === p || path_only.startsWith(p + '/'))) return '/dashboard';
  return raw;
}

type State =
  | { kind: 'checking' }
  | { kind: 'has_local'; identity: Identity }
  | { kind: 'restoring' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string; previous: State };

export default function LoginPage() {
  // Same Suspense reason as /signup — useSearchParams needs the
  // boundary in Next 15 prerender.
  return (
    <Suspense fallback={
      <main className="container-x py-24 text-sm text-ink-dim text-center">Loading…</main>
    }>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const router = useRouter();
  const search = useSearchParams();
  const next   = safeNext(search?.get('next') ?? null);
  const [state, setState]   = useState<State>({ kind: 'checking' });
  const [wifInput, setWif]  = useState('');

  // Probe IndexedDB on mount — if the device already has an identity,
  // skip the WIF restore form and just offer "continue as <pubkey>".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await loadIdentity();
        if (cancelled) return;
        setState(id ? { kind: 'has_local', identity: id } : { kind: 'restoring' });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: (err as Error).message, previous: { kind: 'restoring' } });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function continueAs(identity: Identity) {
    setState({ kind: 'submitting' });
    try {
      // Idempotent — if the account already exists this is a no-op fetch
      // that returns the existing row. Always call it on login so a
      // pruned-and-revisited identity gets re-created seamlessly.
      await createAccount(identity.account_id);
      router.push(next);
    } catch (err) {
      const msg = err instanceof ApiCallFailed
        ? `${err.status}: ${err.body && 'error' in err.body ? err.body.error : 'request failed'}`
        : (err as Error).message;
      setState({ kind: 'error', message: msg, previous: { kind: 'has_local', identity } });
    }
  }

  async function restoreFromWif() {
    if (!wifInput.trim()) return;
    try {
      const identity = identityFromWif(wifInput.trim());
      await saveIdentity(identity);
      await continueAs(identity);
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message, previous: { kind: 'restoring' } });
    }
  }

  return (
    <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center container-x py-16">
      <div className="w-full max-w-md card">
        <h1 className="heading-2">Log in.</h1>

        {state.kind === 'checking' && (
          <p className="mt-6 text-sm text-ink-dim">Checking this device for an existing keypair…</p>
        )}

        {state.kind === 'has_local' && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-ink-dim">
              Found a keypair on this device. Continue as:
            </p>
            <code className="block font-mono text-xs text-accent break-all">
              {state.identity.account_id}
            </code>
            <button onClick={() => continueAs(state.identity)} className="btn-primary w-full">
              Continue as {shortAccountId(state.identity.account_id)}
            </button>
            <button onClick={() => setState({ kind: 'restoring' })}
                    className="btn-secondary w-full text-sm">
              Use a different keypair (restore from WIF)
            </button>
          </div>
        )}

        {state.kind === 'restoring' && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-ink-dim">
              Paste your WIF recovery key. The dashboard will derive the matching pubkey, save
              the keypair to this browser, and resume your account.
            </p>
            <textarea
              value={wifInput}
              onChange={(e) => setWif(e.target.value)}
              placeholder="L4WIF…"
              rows={3}
              className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
            />
            <button
              onClick={restoreFromWif}
              disabled={!wifInput.trim()}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Restore
            </button>
          </div>
        )}

        {state.kind === 'submitting' && (
          <p className="mt-6 text-sm text-ink-dim text-center">Resolving account…</p>
        )}

        {state.kind === 'error' && (
          <div className="mt-6 rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-4">
            <div className="text-sm font-medium text-red-300">Something went wrong</div>
            <p className="mt-1 text-xs text-red-200/80 break-words">{state.message}</p>
            <button onClick={() => setState(state.previous)}
                    className="btn-secondary text-sm mt-4">Try again</button>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/5 text-center text-sm">
          <span className="text-ink-dim">No account on this device?</span>{' '}
          <Link href="/signup" className="text-accent hover:text-accent-muted">Get started</Link>
        </div>
      </div>
    </main>
  );
}
